// contexts/AuthContext.tsx

import React, { createContext, useContext, useMemo, useEffect } from "react";
import {
  useSessionContext,
  useUser as useSupabaseUser,
} from "@supabase/auth-helpers-react";
import { Session } from "@supabase/supabase-js";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { usePermissions, Permissions } from "../hooks/usePermissions";
import { User, Official } from "../types";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { queryClient } from "../lib/queryClient";
//import { useRolePermissionDefinitions } from "../hooks/useRolePermissionDefinitions";

// A lightweight query to get only the current user's official record for permissions
function useOfficialIdentities() {
  const supabaseUser = useSupabaseUser();
  return useQuery<Partial<Official>[]>({
    queryKey: ["officialIdentities", supabaseUser?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("officials")
        .select("id, user_id")
        .eq("user_id", supabaseUser!.id);
      if (error) throw error;
      return data.map((o) => ({ id: o.id, userId: o.user_id })) || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!supabaseUser?.id,
  });
}

interface AuthContextType {
  user: User | null;
  permissions: Permissions;
  isLoading: boolean;
  session: Session | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { session, isLoading: isSupabaseAuthLoading } = useSessionContext();
  const supabaseUser = useSupabaseUser();

  const {
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
  } = useCurrentUser(supabaseUser?.id);
  const { data: officials } = useOfficialIdentities();

  const permissions = usePermissions(user, (officials as Official[]) || []);
  // Don't block app render on officials — they're only needed for scope:'own' guards
  // which are not used for general navigation permission checks.
  // Officials load in the background and permissions re-evaluate via useMemo when ready.
  const isLoading =
    isSupabaseAuthLoading ||
    isUserLoading ||
    isUserFetching;

  const value = useMemo(
    () => ({
      user: user || null,
      permissions,
      isLoading,
      session,
    }),
    [user, permissions, isLoading, session]
  );

  useEffect(() => {
    // Only expose session data in development for debugging
    if (typeof window !== "undefined" && import.meta.env.DEV) {
      (window as any).__ACTIVE_USER_ID__ = user?.id ?? null;
      (window as any).__HAS_SESSION__ = !!session;
    }
  }, [user?.id, session]);

  useEffect(() => {
    // Check for an active session on mount to handle edge cases
    const checkSession = async () => {
      try {
        const {
          data: { session: currentSession },
          error,
        } = await supabase.auth.getSession();
        if (error) {
          console.error("Error checking session:", error);
        } else if (currentSession && !session) {
          console.log(
            "Session found via getSession but not useSession yet",
            currentSession
          );
        }
      } catch (err) {
        console.error("Unexpected error checking session:", err);
      }
    };

    checkSession();

    // Listen for auth changes to debug and ensure stability
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, _session) => {
      if (event === "TOKEN_REFRESHED") {
        console.log("Token refreshed successfully");
      }
      if (event === "SIGNED_OUT") {
        console.log("User signed out — clearing query cache");
        queryClient.clear();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [session]);

  // Proactive session validation when tab regains focus
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;

      try {
        const { error } = await supabase.auth.getUser();
        if (error) {
          console.warn("Session invalid on tab focus — signing out", error.message);
          await supabase.auth.signOut();
        }
      } catch (err) {
        // Network error — don't sign out for connectivity issues
        console.warn("Could not validate session (network error)", err);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
